<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

import SimpleButton from "@/components/ui/SimpleButton.vue";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import {
  getReportName,
  getUserFriendlyName,
  isCrossValidationReport,
  type CrossValidationReportDto,
  type EstimatorReportDto,
  type UserDto,
} from "@/dto";
import { twoColumnLayoutToggleLeftKey } from "@/injectionKeys";
import { useClipboard } from "@/services/clipboard";
import { useUsers } from "@/services/users";
import { formatDatetime } from "@/services/utils";
import { useDatasourceStore } from "@/stores/datasource";
import DatasourceToggle from "@/views/DatasourceToggle.vue";
import ReportSummarySplitSelector from "@/views/reports/ReportSummarySplitSelector.vue";

const { report } = defineProps<{
  report: EstimatorReportDto | CrossValidationReportDto;
}>();

const users = useUsers();
const clipboard = useClipboard();
const route = useRoute();
const datasource = useDatasourceStore();

const tenantName = computed(() => route.params.workspacePublicId as string);
const projectName = computed(() => route.params.projectName as string);

const selectedSplitIndex = defineModel<number>("selectedSplitIndex", { required: false });
const creator = ref<UserDto | null>(null);
const isHidden = ref(false);
const showMoreInfo = ref(false);

const toggleLeft = inject(twoColumnLayoutToggleLeftKey);

const metricsTocItems = computed(() => {
  const seenNames = new Set();
  return report.medias.filter((m) => {
    if (m.category !== "performance") return false;
    if (seenNames.has(m.name)) return false;
    seenNames.add(m.name);
    return true;
  });
});

const inspectionMethods = computed(() => {
  return report.medias
    .filter((m) => m.category === "inspection")
    .filter((m) => m.data_source === datasource.sourceName || m.data_source === null)
    .map((m) => [m.verbose_name.replace("Inspection - ", ""), m.name]);
});

async function fetchCreator() {
  const user = await users.getUser(report.creator_id);
  if (user) {
    creator.value = user;
  }
}

async function copyReportId() {
  await clipboard.copyText(report.urn);
}

async function copyReportSnippet() {
  const snippet = `import skore

project = skore.Project("hub://${tenantName.value}/${projectName.value}")
report = project.get("${report.urn}")`;
  await clipboard.copyText(snippet);
}

function toggleLeftColumn() {
  isHidden.value = !isHidden.value;
  toggleLeft?.();
}

onMounted(async () => {
  await fetchCreator();
});
</script>

<template>
  <div class="report-summary card" :class="{ hidden: isHidden }">
    <header>
      <div class="report-name text-sm-medium">
        <i class="icon icon-test-tube text-base-medium" />
        {{ getReportName(report) }}
      </div>

      <div
        class="hide text-base-regular"
        role="button"
        tabindex="0"
        @click="toggleLeftColumn"
        @keydown.enter="toggleLeftColumn"
        @keydown.space.prevent="toggleLeftColumn"
      >
        <i class="icon icon-sidebar" />
        <span class="hide-text">{{ isHidden ? "Show" : "Hide" }}</span>
      </div>
    </header>

    <div class="card-content text-base-regular">
      Check how well your model performs at a glance.

      <span v-if="showMoreInfo">
        View key metrics, compare them against your goals, and interact with live plots to see where
        your predictions shine, or need improvement.
      </span>

      <a class="more-info" @click="showMoreInfo = !showMoreInfo">
        {{ showMoreInfo ? "Less..." : "More..." }}
      </a>
    </div>

    <div class="card-content text-base-regular metadata">
      <div class="metadatum text-base-regular">
        <div class="name"><i class="icon icon-calendar" /> Created at</div>
        <div class="value">{{ formatDatetime(report.created_at) }}</div>
      </div>

      <div class="metadatum text-base-regular">
        <div class="name"><i class="icon icon-user" /> Author</div>
        <div class="value">
          <UserAvatar v-if="creator" :name="getUserFriendlyName(creator)" />
        </div>
      </div>

      <div class="metadatum text-base-regular">
        <div class="name"><i class="icon icon-barcode" /> Report ID</div>
        <div class="value">
          <SimpleButton icon-left="icon-copy" kind="ghost" label="Copy ID" @click="copyReportId" />
        </div>
      </div>

      <div class="metadatum text-base-regular">
        <div class="name"><i class="icon icon-code" /> Get estimator</div>
        <div class="value">
          <SimpleButton
            icon-left="icon-copy"
            kind="ghost"
            label="Copy snippet"
            @click="copyReportSnippet"
          />
        </div>
      </div>
    </div>

    <div class="card-content text-base-regular toc">
      <div v-if="isCrossValidationReport(report)" class="section text-sm-regular">
        Cross-validation overview
      </div>
      <div v-if="isCrossValidationReport(report)" class="item">
        <a href="#split-selector">Split selector</a>
      </div>

      <div class="section text-sm-regular">Data overview</div>
      <div class="item"><a href="#table-report">Table Report</a></div>
      <div class="item sub-item">
        <a href="#table-report-table">Table</a>
      </div>
      <div class="item sub-item">
        <a href="#table-report-stats">Stats</a>
      </div>
      <div class="item sub-item">
        <a href="#table-report-associations">Associations</a>
      </div>

      <div class="section text-sm-regular">Metrics overview</div>
      <div class="item"><a href="#general-metrics">General Metrics</a></div>
      <div class="item sub-item" v-for="metric in metricsTocItems" :key="metric.name">
        <a :href="`#${metric.name.replace(/_/g, '-')}`">{{ metric.verbose_name }}</a>
      </div>

      <div class="section text-sm-regular">Model overview</div>
      <div class="item"><a href="#pipeline">Pipeline</a></div>
      <div v-for="[verboseMethodName, name] in inspectionMethods" class="item sub-item" :key="name">
        <a :href="`#inspection-${name}`">{{ verboseMethodName }}</a>
      </div>
    </div>

    <div
      v-if="isCrossValidationReport(report) && selectedSplitIndex !== undefined"
      class="card-content"
    >
      <ReportSummarySplitSelector
        :report="report as CrossValidationReportDto"
        v-model:selected-split-index="selectedSplitIndex"
      />
    </div>

    <div class="card-content text-base-regular">
      <DatasourceToggle variant="sliding" />
    </div>
  </div>
</template>

<style scoped>
.report-summary {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-4);
  transition: gap var(--animation-duration) var(--animation-easing);

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--numbers-12) var(--numbers-16);
    color: var(--content-brand);
    transition: justify-content var(--animation-duration) var(--animation-easing);

    > div {
      display: flex;
      align-items: center;
      gap: var(--numbers-8);

      &:first-child {
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .report-name .icon {
      vertical-align: text-top;
    }

    .hide {
      min-width: 0;
      max-width: 100%;
      flex: 0 1 auto;
      border-radius: var(--numbers-4);
      cursor: pointer;

      &:focus-visible {
        box-shadow:
          0 0 0 0.25rem rgb(6 43 205 / 30%),
          0 0 0 0.125rem rgb(255 255 255 / 30%);
        outline: none;
      }

      .icon {
        flex-shrink: 0;
        transition: transform var(--animation-duration) var(--animation-easing);
      }

      .hide-text {
        overflow: hidden;
        min-width: 0;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .more-info {
    color: var(--content-secondary);
    cursor: pointer;
    text-decoration: underline;
  }

  .metadata {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-16);

    .metadatum {
      display: flex;
      flex-flow: row wrap;
      align-items: center;
      justify-content: space-between;

      .name {
        color: var(--content-secondary);

        i {
          padding-right: var(--numbers-8);
          vertical-align: text-top;
        }
      }

      .value {
        color: var(--content-primary);

        .skore-button {
          height: var(--numbers-32);
        }
      }
    }
  }

  .toc {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-8);

    .section {
      color: var(--content-tertiary);
    }

    .item {
      padding-left: var(--numbers-10);

      a,
      a:visited {
        color: inherit;
        text-decoration: none;
      }
    }

    .sub-item {
      padding-left: var(--numbers-24);
      color: var(--content-secondary);
    }
  }

  .card-content,
  .report-name {
    overflow: hidden;
    transition:
      border-width var(--animation-duration) var(--animation-easing),
      height var(--animation-duration) var(--animation-easing),
      opacity var(--animation-duration) var(--animation-easing),
      padding var(--animation-duration) var(--animation-easing),
      width var(--animation-duration) var(--animation-easing);
  }

  &.hidden {
    gap: 0;

    header {
      justify-content: center;

      .hide {
        .icon {
          transform: rotateY(180deg);
        }
      }
    }

    .card-content,
    .report-name {
      width: 0;
      height: 0;
      padding: 0;
      border-width: 0;
      opacity: 0;
    }
  }
}
</style>
